import { useI18n } from '@i18n';
import React, { lazy, Suspense } from 'react';
import { Navigate, Outlet, useRoutes } from 'react-router-dom';
import { BuildingFiltersProvider } from '@/components/building-filters/BuildingFiltersProvider';
import AdminAccessRequired from '@/components/error-boundaries/AdminErrorBoundary';
import PageErrorBoundary from '@/components/error-boundaries/PageErrorBoundary';
import { FeatureFlagRoutingGuard } from '@/components/FeatureFlagRoutingGuard';
import LoadableContentArea from '@/components/LoadableContentArea';
import LoadingScreen from '@/components/LoadingScreen';
import { useSession } from '@/components/session/useSession';
import { DataCollectionFilterContextProvider } from '@/contexts/DataCollectionFiltersContext';
import { ScenariosListContextProvider } from '@/contexts/ScenariosListContext';
import { DashboardLayout } from '@/layouts/dashboard/DashboardLayout';
import { NavBar } from '@/layouts/dashboard/navbar/NavBar';
import LogoOnlyLayout from '@/layouts/LogoOnlyLayout';
import { PATHS } from '@/routes/paths';
import { getRedirects } from '@/routes/redirects';
import { Redirect } from '@/routes/Redirects/Redirect';
import { RedirectEconomicUnitsTab } from '@/routes/Redirects/RedirectEconomicUnitsTab';
import { RedirectToStatusQuoAnalysisBuilding } from '@/routes/Redirects/RedirectToStatusQuoAnalysisBuilding';
import { RedirectToStatusQuoFirstEconomicUnit } from '@/routes/Redirects/RedirectToStatusQuoFirstEconomicUnit';
import { RedirectToStatusQuoFirstPortfolio } from '@/routes/Redirects/RedirectToStatusQuoFirstPortfolio';
import { StorybookRedirect } from '@/routes/Redirects/StorybookRedirect';
import { DataCollectionEnergySuppliersTabEnum } from '@/routes/search_params/searchParamsEnums';
import { removeRootPath } from '@/routes/utils';
import { BuildingContextProvider } from '@/sections/DataCollection/Building/Context/BuildingContext';
import { ActionPlanContextProvider } from '@/sections/Scenarios/ActionPlan/Context/ActionPlanContext';
import { ScenarioContextProvider } from '@/sections/Scenarios/Scenario/Context/ScenarioContext';
import { StatusQuoAnalysisBuildingContextProvider } from '@/sections/StatusQuoAnalysis/contexts/StatusQuoAnalysisBuildingContext';
import { StatusQuoAnalysisTabAndBenchmarkContextProvider } from '@/sections/StatusQuoAnalysis/contexts/StatusQuoAnalysisTabAndBenchmarkContext';

const Loadable =
  <T extends object>(Component: React.ComponentType<T>) =>
  (props: T) => {
    return (
      <Suspense fallback={<LoadingScreen />}>
        <Component {...props} />
      </Suspense>
    );
  };

const LoadableContentAreaFallback =
  <T extends object>(Component: React.ComponentType<T>) =>
  (props: T) => {
    return (
      <Suspense fallback={<LoadableContentArea />}>
        <Component {...props} />
      </Suspense>
    );
  };

/* Lazy-loaded page components */
/******** Status Quo ********/
const StatusQuoAnalysisBuilding = LoadableContentAreaFallback(
  lazy(() => import('../pages/StatusQuoAnalysis/StatusQuoAnalysisBuilding')),
);

// Shared Status Quo
const SharedStatusQuoBuilding = Loadable(lazy(() => import('../pages/StatusQuoAnalysis/SharedStatusQuoBuilding')));

//. StatusQuoAnalysisEconomicUnits need special handling for the NotActivated wrapper
const StatusQuoAnalysisEconomicUnits = LoadableContentAreaFallback(
  lazy(() => import('../pages/StatusQuoAnalysis/StatusQuoAnalysisEconomicUnits')),
);
const StatusQuoAnalysisEconomicUnitsNotActivated = LoadableContentAreaFallback(
  lazy(() =>
    import('../pages/StatusQuoAnalysis/StatusQuoAnalysisEconomicUnits').then((module) => ({
      default: module.StatusQuoAnalysisEconomicUnitsNotActivated,
    })),
  ),
);
const StatusQuoAnalysisPortfolios = LoadableContentAreaFallback(
  lazy(() => import('../pages/StatusQuoAnalysis/StatusQuoAnalysisPortfolios')),
);
const StatusQuoAnalysisNoBuildingAvailablePage = LoadableContentAreaFallback(
  lazy(() =>
    import('../pages/StatusQuoAnalysis/StatusQuoAnalysisNoBuildingAvailable').then((module) => ({
      default: module.StatusQuoAnalysisNoBuildingAvailable,
    })),
  ),
);

/******** Action Planning ********/
const ActionPlanningActionPlan = Loadable(lazy(() => import('../pages/ActionPlanning/ActionPlanningActionPlan')));
const ActionPlanningScenario = Loadable(lazy(() => import('../pages/ActionPlanning/ActionPlanningScenario')));
const ActionPlanningScenarios = Loadable(lazy(() => import('../pages/ActionPlanning/ActionPlanningScenarios')));

/******** Reporting ********/
const ReportingReport = Loadable(lazy(() => import('../pages/Reporting/ReportingReport')));
const ReportingReports = Loadable(lazy(() => import('../pages/Reporting/ReportingReports')));

/******** Data Collection ********/
// Buildings
const DataCollectionTabs = Loadable(lazy(() => import('../pages/DataCollection/DataCollectionTabs')));
const DataCollectionBuilding = Loadable(lazy(() => import('../pages/DataCollection/DataCollectionBuilding')));
const BuildingCreation = Loadable(
  lazy(() => import('../sections/DataCollection/Buildings/BuildingCreation/BuildingCreation')),
);
// Eco units
const DataCollectionEconomicUnits = Loadable(lazy(() => import('../pages/DataCollection/DataCollectionEconomicUnits')));
// Portfolio
const PortfolioManagement = Loadable(lazy(() => import('../pages/Org/PortfolioManagement')));
// Energy Suppliers
const EnergySuppliersPage = Loadable(lazy(() => import('../pages/DataCollection/EnergySuppliers/EnergySuppliersPage')));
const EnergySupplierPage = Loadable(lazy(() => import('../pages/DataCollection/EnergySuppliers/EnergySupplierPage')));
const EnergySupplierDraftPage = Loadable(
  lazy(() => import('../pages/DataCollection/EnergySuppliers/EnergySupplierDraftPage')),
);
// Consumption Hub
const DataCollectionConsumptionHubPage = Loadable(
  lazy(() => import('../pages/DataCollection/DataCollectionConsumptionHubPage')),
);

/******** CBI ********/
const ConsumptionSnapshot = Loadable(
  lazy(() => import('../sections/DataCollection/ConsumptionInvoices/ConsumptionSnapshot')),
);
const DataCollectionAreaConsumption = Loadable(
  lazy(() => import('../pages/DataCollection/DataCollectionAreaConsumption')),
);
const DataCollectionAreaConsumptionDraft = Loadable(
  lazy(() => import('../pages/DataCollection/DataCollectionAreaConsumptionDraft')),
);
const DataCollectionConsumptionInvoicesPage = Loadable(
  lazy(() => import('../pages/DataCollection/DataCollectionConsumptionInvoicesPage')),
);
const ConsumptionDataHubPage = Loadable(lazy(() => import('../pages/DataCollection/ConsumptionDataHubPage')));

/******** Connectors ********/
const ConnectorList = Loadable(lazy(() => import('../pages/Connectors/ConnectorList')));
const ConnectorDetail = Loadable(lazy(() => import('../pages/Connectors/ConnectorDetail')));
const MeterDetail = Loadable(lazy(() => import('../pages/Connectors/MeterDetail')));

/******** Settings ********/
const OrgSettings = Loadable(lazy(() => import('../pages/Org/OrgSettings')));
const StandardEnergyPrices = Loadable(lazy(() => import('../pages/Org/StandardEnergyPrices')));
const UserProfile = Loadable(lazy(() => import('../pages/Org/UserProfile')));
const Users = Loadable(lazy(() => import('../pages/Org/Users')));

/******** Other ********/
const DrivePage = Loadable(lazy(() => import('../pages/Drive/DrivePage')));

export default function Router() {
  const { t } = useI18n();
  const { org } = useSession();
  const orgSector = org.orgSector;

  return useRoutes([
    // SHARED PUBLIC PAGES (no auth) – must be first so /analysis/:hash is matched before the main layout
    {
      path: 'analysis/:hash',
      element: (
        <PageErrorBoundary pageName={t('PageName_StatusQuoAnalysisBuildingView')}>
          <StatusQuoAnalysisTabAndBenchmarkContextProvider>
            <SharedStatusQuoBuilding />
          </StatusQuoAnalysisTabAndBenchmarkContextProvider>
        </PageErrorBoundary>
      ),
    },
    {
      path: PATHS.root(),
      element: (
        <NavBar>
          <DashboardLayout />
        </NavBar>
      ),
      children: [
        {
          path: '',
          element: <RedirectToStatusQuoAnalysisBuilding />,
        },

        // StatusQuo-analysis
        {
          path: removeRootPath(PATHS.statusQuoAnalysis.root()),
          element: <RedirectToStatusQuoAnalysisBuilding />,
        },
        {
          path: removeRootPath(PATHS.statusQuoAnalysis.buildings()),
          element: (
            <PageErrorBoundary pageName={t('PageName_StatusQuoAnalysisBuildingView')}>
              <RedirectToStatusQuoAnalysisBuilding />
            </PageErrorBoundary>
          ),
        },
        {
          path: removeRootPath(PATHS.statusQuoAnalysis.noBuildings()),
          element: (
            <PageErrorBoundary pageName={t('PageName_StatusQuoAnalysisBuildingView')}>
              <StatusQuoAnalysisNoBuildingAvailablePage />
            </PageErrorBoundary>
          ),
        },
        {
          path: removeRootPath(PATHS.statusQuoAnalysis.building({ id: ':id' })),
          element: (
            <PageErrorBoundary pageName={t('PageName_StatusQuoAnalysisBuildingView')}>
              <StatusQuoAnalysisBuildingContextProvider>
                <StatusQuoAnalysisBuilding />
              </StatusQuoAnalysisBuildingContextProvider>
            </PageErrorBoundary>
          ),
        },
        {
          path: removeRootPath(PATHS.statusQuoAnalysis.portfolio()),
          element: (
            <PageErrorBoundary pageName={t('PageName_StatusQuoAnalysisPortfolioView')}>
              <RedirectToStatusQuoFirstPortfolio />
              <StatusQuoAnalysisTabAndBenchmarkContextProvider storageKey="status-quo-portfolio">
                <BuildingFiltersProvider storageKey="status-quo-portfolio">
                  <StatusQuoAnalysisPortfolios />
                </BuildingFiltersProvider>
              </StatusQuoAnalysisTabAndBenchmarkContextProvider>
            </PageErrorBoundary>
          ),
        },
        {
          path: removeRootPath(PATHS.statusQuoAnalysis.economicUnits()),
          element: (
            <PageErrorBoundary pageName={t('PageName_StatusQuoAnalysisEconomicUnitView', { orgSector })}>
              <RedirectToStatusQuoFirstEconomicUnit />
              <StatusQuoAnalysisTabAndBenchmarkContextProvider storageKey="status-quo-economic-units">
                <StatusQuoAnalysisEconomicUnitsNotActivated>
                  <BuildingFiltersProvider storageKey="status-quo-economic-units">
                    <StatusQuoAnalysisEconomicUnits />
                  </BuildingFiltersProvider>
                </StatusQuoAnalysisEconomicUnitsNotActivated>
              </StatusQuoAnalysisTabAndBenchmarkContextProvider>
            </PageErrorBoundary>
          ),
        },

        // Action planning
        {
          path: removeRootPath(PATHS.actionPlanning.scenarios()),
          element: (
            <FeatureFlagRoutingGuard featureFlag="ACTION_PLANNING_MENU_OPTION_ENABLED">
              <PageErrorBoundary pageName={t('PageName_Scenarios')}>
                <ScenariosListContextProvider>
                  <Outlet />
                </ScenariosListContextProvider>
              </PageErrorBoundary>
            </FeatureFlagRoutingGuard>
          ),
          children: [
            {
              path: '',
              element: (
                <ScenarioContextProvider>
                  <ActionPlanningScenarios />
                </ScenarioContextProvider>
              ),
            },
            {
              path: ':scenarioId',
              element: (
                <PageErrorBoundary pageName={t('PageName_Scenario')}>
                  <ScenarioContextProvider>
                    <BuildingFiltersProvider storageKey="action-planning-scenario">
                      <ActionPlanningScenario />
                    </BuildingFiltersProvider>
                  </ScenarioContextProvider>
                </PageErrorBoundary>
              ),
            },
            {
              path: ':scenarioId/action-plans/:stableId',
              element: (
                <PageErrorBoundary pageName={t('PageName_ActionPlan')}>
                  <ScenarioContextProvider>
                    <ActionPlanContextProvider>
                      <ActionPlanningActionPlan />
                    </ActionPlanContextProvider>
                  </ScenarioContextProvider>
                </PageErrorBoundary>
              ),
            },
          ],
        },

        //Reporting
        {
          path: removeRootPath(PATHS.reporting.root()),
          element: <Navigate to={PATHS.reporting.reports()} replace />,
        },
        {
          path: removeRootPath(PATHS.reporting.reports()),
          element: (
            <PageErrorBoundary pageName={t('PageName_Reports')}>
              <ReportingReports />
            </PageErrorBoundary>
          ),
        },
        {
          path: removeRootPath(PATHS.reporting.report({ id: ':id' })),
          element: (
            <PageErrorBoundary pageName={t('PageName_Report')}>
              <ReportingReport />
            </PageErrorBoundary>
          ),
        },

        // Drive
        {
          path: removeRootPath(PATHS.drive.root()),
          element: (
            <PageErrorBoundary pageName={t('PageName_Drive')}>
              <Outlet />
            </PageErrorBoundary>
          ),
          children: [
            {
              path: '',
              element: <DrivePage />,
            },
            {
              path: 'folder/:id',
              element: <DrivePage />,
            },
          ],
        },

        //Data collection
        {
          path: removeRootPath(PATHS.dataCollection.root()),
          element: <Navigate to={PATHS.dataCollection.buildings()} replace />,
        },
        {
          path: removeRootPath(PATHS.dataCollection.buildings()),
          element: (
            <RedirectEconomicUnitsTab>
              <DataCollectionFilterContextProvider>
                <BuildingFiltersProvider storageKey="data-collection">
                  <PageErrorBoundary pageName={t('PageName_DataCollection')}>
                    <DataCollectionTabs />
                  </PageErrorBoundary>
                </BuildingFiltersProvider>
              </DataCollectionFilterContextProvider>
            </RedirectEconomicUnitsTab>
          ),
        },
        {
          path: removeRootPath(PATHS.dataCollection.economicUnits()),
          element: (
            <DataCollectionFilterContextProvider>
              <BuildingFiltersProvider storageKey="data-collection">
                <PageErrorBoundary pageName={t('PageName_DataCollection')}>
                  <DataCollectionEconomicUnits />
                </PageErrorBoundary>
              </BuildingFiltersProvider>
            </DataCollectionFilterContextProvider>
          ),
        },
        {
          path: removeRootPath(PATHS.dataCollection.portfolios()),
          element: (
            <PageErrorBoundary pageName={t('PageName_PortfolioOverview')}>
              <AdminAccessRequired>
                <PortfolioManagement />
              </AdminAccessRequired>
            </PageErrorBoundary>
          ),
        },
        {
          path: removeRootPath(PATHS.dataCollection.building({ id: ':id' })),
          element: (
            <DataCollectionFilterContextProvider>
              <BuildingContextProvider>
                <PageErrorBoundary pageName={t('PageName_BuildingOverview')}>
                  <DataCollectionBuilding />
                </PageErrorBoundary>
              </BuildingContextProvider>
            </DataCollectionFilterContextProvider>
          ),
        },
        {
          path: removeRootPath(PATHS.dataCollection.buildingDraftNew()),
          element: (
            <PageErrorBoundary pageName={t('PageName_DataCollection_BuildingDraft')}>
              <BuildingCreation />
            </PageErrorBoundary>
          ),
        },
        {
          path: removeRootPath(PATHS.dataCollection.buildingDraft({ id: ':id' })),
          element: (
            <PageErrorBoundary pageName={t('PageName_DataCollection_BuildingDraft')}>
              <BuildingCreation />
            </PageErrorBoundary>
          ),
        },
        {
          path: removeRootPath(PATHS.dataCollection.energySuppliers()),
          element: (
            <PageErrorBoundary pageName={t('DataCollection_EnergySupplier', { count: 2 })}>
              <EnergySuppliersPage />
            </PageErrorBoundary>
          ),
        },
        {
          path: removeRootPath(PATHS.dataCollection.energySupplierDrafts()),
          element: (
            <Navigate
              to={PATHS.dataCollection.energySuppliers({ tab: DataCollectionEnergySuppliersTabEnum.DRAFTS })}
              replace
            />
          ),
        },
        {
          path: removeRootPath(PATHS.dataCollection.consumptionHub()),
          element: (
            <FeatureFlagRoutingGuard featureFlag="CONSUMPTION_HUB">
              <PageErrorBoundary pageName={t('PageName_ConsumptionHub')}>
                <DataCollectionConsumptionHubPage />
              </PageErrorBoundary>
            </FeatureFlagRoutingGuard>
          ),
        },
        {
          path: removeRootPath(PATHS.dataCollection.consumptionInvoices()),
          element: (
            <FeatureFlagRoutingGuard featureFlag="CBI_ENABLED">
              <PageErrorBoundary pageName={t('PageName_ConsumptionInvoices')}>
                <DataCollectionConsumptionInvoicesPage />
              </PageErrorBoundary>
            </FeatureFlagRoutingGuard>
          ),
        },
        {
          path: removeRootPath(PATHS.dataCollection.consumptionDataHub()),
          element: (
            <FeatureFlagRoutingGuard featureFlag="CBI_ENABLED">
              <PageErrorBoundary pageName={t('PageName_ConsumptionDataHub')}>
                <ConsumptionDataHubPage />
              </PageErrorBoundary>
            </FeatureFlagRoutingGuard>
          ),
        },
        {
          path: removeRootPath(PATHS.dataCollection.consumptionInvoiceItem({ id: ':id' })),
          element: (
            <FeatureFlagRoutingGuard featureFlag="CBI_ENABLED">
              <PageErrorBoundary pageName={t('PageName_ConsumptionInvoices')}>
                <ConsumptionSnapshot />
              </PageErrorBoundary>
            </FeatureFlagRoutingGuard>
          ),
        },

        // Connectors
        {
          path: removeRootPath(PATHS.connectors.root()),
          element: <Navigate to={PATHS.connectors.list()} replace />,
        },
        {
          path: removeRootPath(PATHS.connectors.list()),
          element: (
            <PageErrorBoundary pageName={t('General_Connectors')}>
              <ConnectorList />
            </PageErrorBoundary>
          ),
        },
        {
          path: removeRootPath(PATHS.connectors.detail(':id')),
          element: (
            <PageErrorBoundary pageName={t('General_Connectors')}>
              <ConnectorDetail />
            </PageErrorBoundary>
          ),
        },
        {
          path: removeRootPath(PATHS.connectors.meter(':connectorId', ':meterId')),
          element: (
            <PageErrorBoundary pageName={t('General_Connectors')}>
              <MeterDetail />
            </PageErrorBoundary>
          ),
        },

        //Org
        {
          path: removeRootPath(PATHS.org.users()),
          element: (
            <PageErrorBoundary pageName={t('PageName_ManageUsers')}>
              <AdminAccessRequired>
                <Users />
              </AdminAccessRequired>
            </PageErrorBoundary>
          ),
        },
        {
          path: removeRootPath(PATHS.org.user({ id: ':id' })),
          element: (
            <PageErrorBoundary pageName={t('PageName_ManageUsers')}>
              <UserProfile />
            </PageErrorBoundary>
          ),
        },
        {
          path: removeRootPath(PATHS.org.settings()),
          element: (
            <PageErrorBoundary pageName={t('PageName_Settings')}>
              <AdminAccessRequired>
                <OrgSettings />
              </AdminAccessRequired>
            </PageErrorBoundary>
          ),
        },
        {
          path: removeRootPath(PATHS.org.standardEnergyPrices()),
          element: (
            <PageErrorBoundary pageName={t('OrgSettings_StandardEnergyPrices_Title')}>
              <AdminAccessRequired>
                <StandardEnergyPrices />
              </AdminAccessRequired>
            </PageErrorBoundary>
          ),
        },
      ],
    },

    // FOCUS PAGES
    {
      path: removeRootPath(PATHS.dataCollection.energySupplierCreate()),
      element: (
        <PageErrorBoundary pageName={t('DataCollection_EnergySupplier', { count: 1 })}>
          <AdminAccessRequired feature="energySupplier">
            <EnergySupplierDraftPage />
          </AdminAccessRequired>
        </PageErrorBoundary>
      ),
    },
    {
      path: removeRootPath(PATHS.dataCollection.energySupplierDraft({ id: ':id' })),
      element: (
        <PageErrorBoundary pageName={t('DataCollection_EnergySupplier', { count: 2 })}>
          <AdminAccessRequired feature="energySupplier">
            <EnergySupplierDraftPage />
          </AdminAccessRequired>
        </PageErrorBoundary>
      ),
    },
    {
      path: removeRootPath(PATHS.dataCollection.energySupplier({ id: ':id' })),
      element: (
        <PageErrorBoundary pageName={t('DataCollection_EnergySupplier', { count: 1 })}>
          <EnergySupplierPage />
        </PageErrorBoundary>
      ),
    },
    {
      path: removeRootPath(PATHS.dataCollection.buildingConsumptionInvoiceDraft({ id: ':id', draftId: ':draftId' })),
      element: (
        <PageErrorBoundary pageName={t('PageName_ConsumptionDraft')}>
          <DataCollectionFilterContextProvider>
            <DataCollectionAreaConsumptionDraft />
          </DataCollectionFilterContextProvider>
        </PageErrorBoundary>
      ),
    },
    {
      path: removeRootPath(
        PATHS.dataCollection.buildingConsumptionInvoice({ id: ':id', consumptionInvoiceId: ':consumptionInvoiceId' }),
      ),
      element: (
        <PageErrorBoundary pageName={t('PageName_Consumption')}>
          <DataCollectionFilterContextProvider>
            <DataCollectionAreaConsumption />
          </DataCollectionFilterContextProvider>
        </PageErrorBoundary>
      ),
    },

    { path: 'storybook', element: <StorybookRedirect /> },

    ...getRedirects().map((redirect) => ({
      path: redirect.from,
      element: <Redirect redirect={redirect} />,
    })),
    {
      path: '*',
      element: <LogoOnlyLayout />,
      children: [
        { path: removeRootPath(PATHS.notFound()), element: <NotFound /> },
        { path: '*', element: <Navigate to={PATHS.notFound()} replace /> },
      ],
    },
    { path: '*', element: <Navigate to={PATHS.notFound()} replace /> },
  ]);
}

const NotFound = Loadable(lazy(() => import('../pages/Page404')));
